import { MantineDataTable } from "@/components/molecule/mantineDataTable/MantineDataTable";
import { MantinePanelTable } from "@/components/molecule/mantinePanelTable/mantinePanelTable";
import PageTemplate from "@/components/template/page/PageTemplate";
import { COMMON_TEXT } from "@/constants/text";
import { useWmsLogsFetch } from "@/fetch/page/wmsLogs/useWmsLogsFetch";
import { usePaginationStore } from "@/store/pagenation.store";
import timeUtil from "@/utils/timeUtil";
import { Flex, Pagination } from "@mantine/core";
import { WlWithEslViewGet200ResponseRowsInnerActionParamParamsItemsInner } from "@sizlcorp/sizl-api-document/dist/models";
import { MRT_ColumnDef } from "mantine-react-table";
import { useMemo } from "react";

type DataType = {
  logNum: number | undefined;
  performanceType: string;
  itemCode: string | undefined;
  itemName: string | undefined;
  lotName: string | undefined | null;
  spec: string | undefined;
  quantity: string | undefined;
  sourceLocationName: string | undefined;
  targetLocationName: string | undefined;
  creatorUserCode: string | undefined;
  createdAt: string;
  items:
    | WlWithEslViewGet200ResponseRowsInnerActionParamParamsItemsInner[]
    | undefined;
};

const WmsLogs = Object.assign(
  {},
  {
    Template: PageTemplate,
    Table: MantinePanelTable,
    Pagination: Pagination,
  }
);

export const WmsLogsPage = () => {
  const { data } = useWmsLogsFetch();

  const rowsWithFromLocationCode = data?.data?.rows?.filter(
    (row) => row.actionParam?.params?.fromLocationCode
  );
  const totalPages = data?.data.totalPages;
  const { currentPage, setPage } = usePaginationStore((state) => ({
    currentPage: state.currentPage,
    setPage: state.setPage,
  }));

  const convertedData: DataType[] | undefined = rowsWithFromLocationCode?.map(
    (row) => {
      return {
        logNum: row.id,
        performanceType: row.actionParam?.params?.targetLocationName
          ? "벌크이동"
          : "벌크출고",
        itemCode: row?.batchLotInfo?.[0]?.itemInfo?.code,
        itemName: row?.batchLotInfo?.[0]?.itemInfo?.name,
        lotName: row?.batchLotInfo?.[0]?.lotInfo?.name,
        spec: row?.batchLotInfo?.[0]?.itemInfo?.spec,
        quantity: row?.batchLotInfo?.[0]?.quantity,
        sourceLocationName: row.actionParam?.params?.sourceLocationName,
        targetLocationName: row.actionParam?.params?.targetLocationName,
        creatorUserCode: row.creatorUserCode,
        createdAt: timeUtil(row.createdAt),
        items: row.actionParam?.params?.items,
      };
    }
  );

  const columns = useMemo<MRT_ColumnDef[]>(
    () => [
      {
        accessorKey: "logNum",
        header: COMMON_TEXT.COLUMN.LOG_NUM,
      },
      {
        accessorKey: "performanceType",
        header: COMMON_TEXT.COLUMN.PERFORMANCE_TYPE,
      },
      {
        accessorKey: "itemCode",
        header: COMMON_TEXT.COLUMN.ITEM_CODE,
      },
      {
        accessorKey: "itemName",
        header: COMMON_TEXT.COLUMN.ITEM_NAME,
      },
      {
        accessorKey: "lotName",
        header: COMMON_TEXT.COLUMN.LOT_NAME,
      },
      {
        accessorKey: "spec",
        header: COMMON_TEXT.COLUMN.SPEC,
      },
      {
        accessorKey: "quantity",
        header: COMMON_TEXT.COLUMN.QUANTITY,
      },
      {
        accessorKey: "sourceLocationName",
        header: COMMON_TEXT.COLUMN.SOURCE_LOCATION,
      },
      {
        accessorKey: "targetLocationName",
        header: COMMON_TEXT.COLUMN.TARGET_LOCATION,
      },
      {
        accessorKey: "creatorUserCode",
        header: COMMON_TEXT.COLUMN.WORKER,
      },
      {
        accessorKey: "createdAt",
        header: COMMON_TEXT.COLUMN.CREATED_AT,
      },
    ],
    []
  );

  const panelColumns = useMemo<MRT_ColumnDef[]>(
    () => [
      {
        accessorKey: "itemCode",
        header: COMMON_TEXT.COLUMN.ITEM_CODE,
      },
      {
        accessorKey: "itemName",
        header: COMMON_TEXT.COLUMN.ITEM_NAME,
      },
      {
        accessorKey: "spec",
        header: COMMON_TEXT.COLUMN.SPEC,
      },
      {
        accessorKey: "lotName",
        header: COMMON_TEXT.COLUMN.LOT_NAME,
      },
      {
        accessorKey: "quantity",
        header: COMMON_TEXT.COLUMN.QUANTITY,
      },
    ],
    []
  );

  return (
    <WmsLogs.Template>
      <WmsLogs.Table
        renderDetailPanel={({ row }) => {
          const { items } = row.original as DataType;

          const convertedData = items?.map((item) => {
            return {
              itemCode: item?.lotInfo?.itemCode ?? "-",
              itemName: item?.lotInfo?.item?.name ?? "-",
              spec: item.lotInfo?.item?.spec ?? "-",
              lotName: item?.lotInfo?.name,
              quantity: item?.quantity ?? "-",
            };
          });

          return (
            <MantineDataTable
              data={convertedData ?? []}
              columns={panelColumns}
            />
          );
        }}
        columns={columns}
        data={convertedData ?? []}
      />
      <Flex direction={"column"} gap={"md"} align={"center"} mt={"xl"}>
        <WmsLogs.Pagination
          total={totalPages || 0}
          value={currentPage}
          onChange={(page) => setPage(page)}
        />
      </Flex>
    </WmsLogs.Template>
  );
};
